import React, { useEffect, useState } from "react";
import Dropdown from "../../components/Dropdown";
import RadioGroup from "../../components/RadioGroup";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { currentUserActions } from "../../reducers/currentUser";
import {
  togglePanel,
  setSelectedStaff,
  clearSelectedStaff,
  fetchStaffBasicData,
  submitEditStaff,
  fetchStaff,
  submitCreateStaff,
  setPassOpen,
  setCurrentPage,
  changeAvatarStaff,
} from "../../reducers/companyStaff";
import AvatarDialog from "./components/AvatarDialog";
import ConfirmPopup from "../../components/ConfirmPopup";
import noAvatarAdmin from "../../../assets/img/noAvatarAdmin.svg";
import noAvatarAdminText from "../../../assets/img/noAvatarAdminText.svg";
import {LoadingSpinner} from "../../components/Icons";
import TextInput from "../../components/TextInput";
import PasswordInput from "../../components/PasswordInput";
import CustomIconButton from "../../components/CustomIconButton";
import CustomButton from "../../components/CustomButton";
import { validatePassword, validateFieldReq, validateEmail } from "../../utils/validation";
import i18n from 'i18next';

const radioOptions = [
  {
    label: "可能",
    value: true,
  },
  {
    label: "不可",
    value: false,
  },
];

interface FormDataState {
  first_name: string;
  last_name: string;
  email: string;
  department: any;
  position: any;
  type: any;
  password: string;
  password2: string;
  can_man_talent: boolean;
  can_man_action: boolean;
  can_man_job: boolean;
  can_man_news: boolean;
  avatar: (Blob | File) | null;
  delete_avatar: boolean;
}

const initialData: FormDataState = {
  first_name: "",
  last_name: "",
  email: "",
  department: null,
  position: null,
  type: null,
  password: "",
  password2: "",
  can_man_talent: true,
  can_man_action: true,
  can_man_job: true,
  can_man_news: true,
  avatar: null,
  delete_avatar: false,
};

interface ModalProps {
  onClose: () => void;
  selectedUser: () => void;
}

const StaffCreate: React.FC<ModalProps> = ({ onClose, selectedUser }) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const navigate = useNavigate();
  const currentUser = useSelector((state: any) => state.currentUser);
  const { type } = currentUser.staffInfo;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [avatarFile, setAvatarFile] = useState<any>(null);
  const [radioOptionsAction, setRadioOptionsAction] = useState<any>(radioOptions);
  const [radioOptionsJob, setRadioOptionsJob] = useState<any>(radioOptions);
  const [radioOptionsNews, setRadioOptionsNews] = useState<any>(radioOptions);

  const { 
    loading,
    isPanelOpen, 
    selectedStaff, 
    staffBasicData, 
    passOpen,
  } = useSelector(
    (state: any) => state.companyStaff,
  );

  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({
    first_name: "",
    last_name: "",
    department: "",
    position: "",
    type: "",
    email: "",
    password: "",
    password2: "",
  });

  useEffect(() => {
    if (selectedStaff) {
      setFormData({
        first_name: selectedStaff.first_name,
        last_name: selectedStaff.last_name,
        email: selectedStaff.email,
        department: selectedStaff.department,
        position: selectedStaff.position,
        type: selectedStaff.staff_type_display,
        password: selectedStaff.password,
        password2: selectedStaff.password2,
        can_man_talent: selectedStaff.can_man_talent,
        can_man_action: selectedStaff.can_man_action,
        can_man_job: selectedStaff.type == "general" ? false : selectedStaff.can_man_job,
        can_man_news: selectedStaff.can_man_news,
        avatar: selectedStaff.avatar ? selectedStaff.avatar : noAvatarAdmin,
        delete_avatar: selectedStaff.delete_avatar,
      });
      assignCanJobByType(selectedStaff.staff_type_display);
    }
    else{
      setFormData(initialData);
      dispatch(setPassOpen(false));
      setRadioOptionsJob(radioOptions)
    }
    if (isPanelOpen) dispatch(fetchStaffBasicData(true));
    setAvatarFile(null);
  }, [selectedStaff]);

  const handleAvatarChange = (file: any) => {
    if (!file && !selectedStaff?.delete_avatar) {
      setAvatarFile(null);
      setIsConfirmOpen(false);
    } else {
      if(selectedStaff){
        const data = {
            id: selectedStaff.id,
            formData: {
                ...selectedStaff,
                department: formData.department.id,
                position: formData.position.id,
                type: formData.type.id,
                avatar: file,
            },
        };
        dispatch(changeAvatarStaff(data));
        setAvatarFile(data.formData.avatar);
      }
      else{
        setAvatarFile(file);
      }
    }
    setDialogOpen(false);
  };

  const handleDeleteCurrentAvatar = () => {
    setIsConfirmOpen(false);
    if(selectedStaff){
      const data = {
          id: selectedStaff.id,
          formData: {
              ...selectedStaff,
              department: formData.department.id,
              position: formData.position.id,
              type: formData.type.id,
              avatar: null,
              delete_avatar: true,
          },
      };
      dispatch(changeAvatarStaff(data));
      setAvatarFile(data.formData.avatar);
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateFormData = () => {
    let formErrors = {
      first_name: "",
      last_name: "",
      department: "",
      position: "",
      type: "",
      email: "",
      password: "",
      password2: "",
    };

    const validateLastnameRes = validateFieldReq(formData.last_name, "姓");
    if(validateLastnameRes != true) {
      formErrors.last_name = String(validateLastnameRes);
    }

    const validateFirstnameRes = validateFieldReq(formData.first_name, "名");
    if(validateFirstnameRes != true) {
      formErrors.first_name = String(validateFirstnameRes);
    }

    const validateDepartmentRes = validateFieldReq(formData.department, "所属");
    if(validateDepartmentRes != true) {
      formErrors.department = String(validateDepartmentRes);
    }

    const validatePositionRes = validateFieldReq(formData.position, "役職");
    if(validatePositionRes != true) {
      formErrors.position = String(validatePositionRes);
    }

    const validateTypeRes = validateFieldReq(formData.type, "権限");
    if(validateTypeRes != true) {
      formErrors.type = String(validateTypeRes);
    }
    
    const validateEmailRes = validateEmail(formData.email);
    if(validateEmailRes != true) {
      formErrors.email = String(validateEmailRes);
    }

    if (!selectedStaff || passOpen) {
      const validatePasswordRes = validatePassword(formData.password)
      if (validatePasswordRes != true) {
        formErrors.password = String(validatePasswordRes);
      }   
      
      const validatePassword2Res = validatePassword(formData.password2)
      if (validatePassword2Res != true) {
        formErrors.password2 = String(validatePassword2Res);
      }

      if (formData.password != formData.password2 && formErrors.password2 == "") {
        formErrors.password2 = i18n.t('MSG_GLOBAL_INCORRECT_PASSWORD');
      }
    }

    setErrors(formErrors);
    return !Object.values(formErrors).some((error) => error);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateFormData()) {
      if (selectedStaff) {
        const data = {
          id: selectedStaff.id,
          formData: {
            ...formData,
            department: formData.department.id,
            position: formData.position.id,
            type: formData.type.id,
            //avatar: avatarFile,
            delete_avatar: false,
          },
        };
        dispatch(submitEditStaff(data));
      } else {
        const data = {
          formData: {
            ...formData,
            department: formData.department.id,
            position: formData.position.id,
            type: formData.type.id,
            avatar: avatarFile,
            delete_avatar: false,
          },
        };
        dispatch(submitCreateStaff(data));
      }
    }
  };

  onClose = () => {
    dispatch(clearSelectedStaff());
  };

  const staffBasicDataDepartment = staffBasicData?.departments;
  const staffBasicDataPosition = staffBasicData?.positions;
  const staffBasicDataType = staffBasicData?.type;

  const setSelectedRadioValue = (name: any, value: any) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleChangeType = (value: any) => {
    setFormData((prev) => ({ ...prev, type: value }));
    assignCanJobByType(value);
  }

  const assignCanJobByType = (value: any) => {
    if(value.id == 'general') {
      setFormData((prev) => ({ ...prev, can_man_job: false, can_man_news: false }));
      const dataJob = radioOptionsJob.map((item: any) => ({...item, disabled: true}));
      setRadioOptionsJob(dataJob);
      const dataNews = radioOptionsNews.map((item: any) => ({...item, disabled: true}));
      setRadioOptionsNews(dataNews);
    }
    else{
      const dataJob = radioOptionsJob.map((item: any) => ({...item, disabled: false}));
      setRadioOptionsJob(dataJob);
      const dataNews = radioOptionsNews.map((item: any) => ({...item, disabled: false}));
      setRadioOptionsNews(dataNews);
    }
  }

  const handleSetPassOpen = () => {
    dispatch(setPassOpen(true));
  };

  if (!isPanelOpen && !selectedStaff) return null;

  return (
    <>
      {dialogOpen && (
        <AvatarDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onConfirm={handleAvatarChange}
          onConfirmPopupOpen={() => setIsConfirmOpen(true)}
        />
      )}
      {isConfirmOpen && (
        <ConfirmPopup
          open={isConfirmOpen}
          onClose={() => setIsConfirmOpen(false)}
          confirmContent={"現在使用しているプロフィール画像を削除しますか？"}
          onConfirm={handleDeleteCurrentAvatar}
        />
      )}
      <div className="w-[410px] bg-white rounded-2xl pt-3 alu-panel-box-shadow h-full">
        <div className="flex items-center justify-between px-4 mb-1">
          <h2 className="text-xl leading-[1.875rem] font-bold alu-text-base-2">
            管理ユーザーを{`${!selectedStaff ? "追加" : "編集"}`}
          </h2>
          <CustomIconButton onClick={onClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <g clipPath="url(#clip0_8574_265)">
                <path
                  d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                  fill="currentColor"
                />
              </g>
              <defs>
                <clipPath id="clip0_8574_265">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </CustomIconButton>
        </div>
        <div className="overflow-y-auto custom-scrollbar h-[calc(100%-40px)] px-4 pb-4">
          <form onSubmit={handleSubmit} className="mt-6 w-full flex flex-col">
            <div className="mb-6">
              <div 
                className={`relative w-20 h-20 flex items-center justify-center rounded-full ${
                  !formData.avatar ? "border border-[#BBB]" : ""
                } bg-white hover:cursor-pointer`}
                onClick={(e: any) => {
                  e.preventDefault();
                  setDialogOpen(true)
                }}
              >
                {(formData.avatar || avatarFile) ? (
                  <>
                    <img
                      src={`${avatarFile ? URL.createObjectURL(avatarFile) : formData.avatar}`}
                      alt="avatar"
                      className="w-full h-full rounded-full object-cover"
                    />
                    {!selectedStaff?.avatar && <img src={noAvatarAdminText} alt="" className="absolute left-0 bottom-0 w-10 h-5" />}
                    <button className="absolute bottom-[-12px] right-[-8px] rounded-full p-1 bg-white">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <g clipPath="url(#clip0_14389_17780)">
                          <path
                            d="M10.0002 12.6654C11.4729 12.6654 12.6668 11.4715 12.6668 9.9987C12.6668 8.52594 11.4729 7.33203 10.0002 7.33203C8.5274 7.33203 7.3335 8.52594 7.3335 9.9987C7.3335 11.4715 8.5274 12.6654 10.0002 12.6654Z"
                            fill="#808080"
                          />
                          <path
                            d="M7.49984 1.66797L5.97484 3.33464H3.33317C2.4165 3.33464 1.6665 4.08464 1.6665 5.0013V15.0013C1.6665 15.918 2.4165 16.668 3.33317 16.668H16.6665C17.5832 16.668 18.3332 15.918 18.3332 15.0013V5.0013C18.3332 4.08464 17.5832 3.33464 16.6665 3.33464H14.0248L12.4998 1.66797H7.49984ZM9.99984 14.168C7.69984 14.168 5.83317 12.3013 5.83317 10.0013C5.83317 7.7013 7.69984 5.83463 9.99984 5.83463C12.2998 5.83463 14.1665 7.7013 14.1665 10.0013C14.1665 12.3013 12.2998 14.168 9.99984 14.168Z"
                            fill="#808080"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_14389_17780">
                            <rect width="20" height="20" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                  </>
                ) : (
                  <span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_14389_17780)">
                        <path
                          d="M10.0002 12.6654C11.4729 12.6654 12.6668 11.4715 12.6668 9.9987C12.6668 8.52594 11.4729 7.33203 10.0002 7.33203C8.5274 7.33203 7.3335 8.52594 7.3335 9.9987C7.3335 11.4715 8.5274 12.6654 10.0002 12.6654Z"
                          fill="#808080"
                        />
                        <path
                          d="M7.49984 1.66797L5.97484 3.33464H3.33317C2.4165 3.33464 1.6665 4.08464 1.6665 5.0013V15.0013C1.6665 15.918 2.4165 16.668 3.33317 16.668H16.6665C17.5832 16.668 18.3332 15.918 18.3332 15.0013V5.0013C18.3332 4.08464 17.5832 3.33464 16.6665 3.33464H14.0248L12.4998 1.66797H7.49984ZM9.99984 14.168C7.69984 14.168 5.83317 12.3013 5.83317 10.0013C5.83317 7.7013 7.69984 5.83463 9.99984 5.83463C12.2998 5.83463 14.1665 7.7013 14.1665 10.0013C14.1665 12.3013 12.2998 14.168 9.99984 14.168Z"
                          fill="#808080"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_14389_17780">
                          <rect width="20" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </span>
                )}
              </div>
            </div>
            <div className="mb-6">
              <label
                className="block mb-2 text-sm font-bold alu-text-base-2"
                htmlFor="fullName"
              >
                氏名
              </label>
              <div className="flex items-center gap-2">
                <div className="w-full">
                  <TextInput
                    error={errors.last_name}
                    errorText={errors.last_name}
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="姓"
                  />
                </div>
                <div className="w-full">
                  <TextInput
                    error={errors.first_name}
                    errorText={errors.first_name}
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="名"
                  />
                </div>
              </div>
            </div>
            <div className="mb-6">
              <label
                className="block mb-2 text-sm font-bold alu-text-base-2 pointer-events-none"
                htmlFor="email"
              >
                メールアドレス
              </label>
              <TextInput
                error={errors.email}
                errorText={errors.email}
                type="text"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="sample@sample.com"
              />
            </div>
            {selectedStaff && (
              <CustomButton
                type="button"
                onClick={handleSetPassOpen}
                customStyle="inline-block mb-6"
                variant="text"
              >
                {!passOpen ? "パスワードを変更" : "パスワード変更をキャンセル"}
              </CustomButton>
            )}
            {(!selectedStaff || passOpen) && (
              <div className="mb-6">
                <label
                  className="block mb-2 text-sm font-bold alu-text-base-2 pointer-events-none"
                  htmlFor="passsword"
                >
                  パスワード
                </label>
                <PasswordInput
                  error={errors.password.length > 0}
                  errorText={errors.password}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="8文字以上で英数字を含めてください"
                />
              </div>
            )}
            {(!selectedStaff || passOpen) && (
              <div className="mb-6">
                <label
                  className="block mb-2 text-sm font-bold alu-text-base-2 pointer-events-none"
                  htmlFor="password2"
                >
                  パスワードの確認
                </label>
                <PasswordInput
                  error={errors.password2.length > 0}
                  errorText={errors.password2}
                  id="password2"
                  name="password2"
                  value={formData.password2}
                  onChange={handleChange}
                  placeholder="確認のためもう一度入力してください"
                />
              </div>
            )}
            <div className="mb-6">
              <label
                className="block mb-2 text-sm font-bold alu-text-base-2 pointer-events-none"
                htmlFor="department"
              >
                所属
              </label>
              <Dropdown
                placeholder={"選択してください"}
                options={staffBasicDataDepartment}
                disabled={!staffBasicDataDepartment || staffBasicDataDepartment.length === 0}
                value={formData.department}
                onChange={(value: any) =>
                  setFormData((prev) => ({ ...prev, department: value }))
                }
                customStyle={errors.department ? "!border-[#F33D62]" : ""}
              />
              {errors.department && <p className="text-[#F33D62] text-sm mt-2">
                {errors.department}
              </p>}
            </div>
            <div className="mb-6">
              <label
                className="block mb-2 text-sm font-bold alu-text-base-2 pointer-events-none"
                htmlFor="post"
              >
                役職
              </label>
              <Dropdown
                placeholder={"選択してください"}
                options={
                  formData.department && staffBasicDataPosition
                    ? staffBasicDataPosition.filter(position => position.department.id === formData.department.id) 
                    : [] 
                }
                disabled={!formData.department}
                value={formData.position}
                onChange={(value: any) =>
                  setFormData((prev) => ({ ...prev, position: value }))
                }
                customStyle={errors.position ? "!border-[#F33D62]" : ""}
              />
              {errors.position && <p className="text-[#F33D62] text-sm mt-2">
                {errors.position}
              </p>}
            </div>
            <div className="mb-6">
              <label
                className="block mb-2 text-sm font-bold alu-text-base-2"
                htmlFor="type"
              >
                権限
              </label>
              <Dropdown
                placeholder={"選択してください"}
                options={staffBasicDataType}
                value={formData.type}
                onChange={(value: any) => handleChangeType(value)}
                customStyle={errors.type ? "!border-[#F33D62]" : ""}
              />
              {errors.type && <p className="text-[#F33D62] text-sm mt-2">
                {errors.type}
              </p>}
            </div>
            <div className="mb-6">
              <label
                className="block mb-2 text-sm font-bold alu-text-base-2"
                htmlFor="view_reference"
              >
                アクション管理画面の閲覧
              </label>
              <RadioGroup
                name={"view_reference"}
                options={radioOptionsAction}
                value={formData.can_man_action}
                onChange={(value: any) => setSelectedRadioValue("can_man_action", value)}
              />
            </div>
            <div className="mb-6">
              <label
                className="block mb-2 text-sm font-bold alu-text-base-2"
                htmlFor="view_job_info"
              >
                求人情報の配信
              </label>
              <RadioGroup
                name={"view_job_info"}
                options={radioOptionsJob}
                value={formData.can_man_job}
                onChange={(value: any) => setSelectedRadioValue("can_man_job", value)}
              />
            </div>
            <div className="mb-10">
              <label
                className="block mb-2 text-sm font-bold alu-text-base-2"
                htmlFor="post_news"
              >
                ニュースの配信
              </label>
              <RadioGroup
                name={"post_news"}
                options={radioOptionsNews}
                value={formData.can_man_news}
                onChange={(value: any) => setSelectedRadioValue("can_man_news", value)}
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <CustomButton
                type="button"
                onClick={() => onClose()}
                variant="outline"
                width="full"
              >
                キャンセル
              </CustomButton>
              <CustomButton
                type="submit"
                width="full"
                disabled={loading}
              >
                <p>{loading ? "保存中..." : (selectedStaff ? '更新' : '追加')}</p>
                {loading && (
                  <span><LoadingSpinner /></span>
                )}
              </CustomButton>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default StaffCreate;
